import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./Dashboard.css";

/* =============================================================
   4XLC Live Agent Dashboard — Steampunk Carnival Cockpit
   Pulls live data from 5 backend agents and renders it as
   gauges, bar mixers, tickers, video-card queues and gadgets.
   ============================================================= */

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const REFRESH_MS = 30_000;

const AGENTS = [
  { key: "marketSignals",  endpoint: "market-signals",  label: "Market Signals" },
  { key: "offerBuilder",   endpoint: "offer-builder",   label: "Offer Builder" },
  { key: "youtubeContent", endpoint: "youtube-content", label: "YouTube Content" },
  { key: "leadMagnet",     endpoint: "lead-magnet",     label: "Lead Magnet" },
  { key: "webCrawler",     endpoint: "web-crawler",     label: "Web Crawler" },
];

/* --------------------- fonts injected once --------------------- */
function FontInjector() {
  useEffect(() => {
    const id = "dash-fonts";
    if (document.getElementById(id)) return;
    const link = document.createElement("link");
    link.id = id; link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Bungee+Shade&family=Major+Mono+Display&family=Special+Elite&family=VT323&display=swap";
    document.head.appendChild(link);
  }, []);
  return null;
}

/* --------------------- carnival bulb string --------------------- */
function BulbString() {
  const bulbs = useMemo(() => {
    const colors = ["#ffc857", "#5cf3ff", "#ff3ec8", "#c6ff3a", "#ff5b4a"];
    return Array.from({ length: 28 }, (_, i) => ({
      x: (i + 0.5) * (1400 / 28),
      y: 30 + Math.sin((i / 27) * Math.PI * 3) * 22,
      c: colors[i % colors.length],
      d: (1.2 + (i % 5) * 0.3).toFixed(2),
      b: (i * 0.07).toFixed(2),
    }));
  }, []);
  return (
    <div className="bulb-string" aria-hidden="true">
      <svg viewBox="0 0 1400 60" preserveAspectRatio="none">
        <path d="M0,30 C200,60 400,0 700,30 C1000,60 1200,0 1400,30" stroke="#3a230b" strokeWidth="2" fill="none" />
        {bulbs.map((b, i) => (
          <g key={i}>
            <circle cx={b.x} cy={b.y} r="4" fill={b.c}>
              <animate attributeName="opacity" values="1;.25;1" dur={`${b.d}s`} repeatCount="indefinite" begin={`${b.b}s`} />
            </circle>
            <circle cx={b.x} cy={b.y} r="9" fill={b.c} opacity=".18">
              <animate attributeName="r" values="9;13;9" dur="2s" repeatCount="indefinite" begin={`${b.b}s`} />
            </circle>
          </g>
        ))}
      </svg>
    </div>
  );
}

/* --------------------- gauge (single arc + needle, no concentric rings) --------------------- */
function Gauge({ value = 0, label = "TREND", color = "#5cf3ff", testid }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  const angle = -90 + (v / 100) * 180; // -90 to +90
  return (
    <div className="gauge" data-testid={testid}>
      <svg viewBox="0 0 200 110">
        <defs>
          <linearGradient id={`gg-${label}`} x1="0" x2="1">
            <stop offset="0" stopColor="#ff5b4a" />
            <stop offset=".4" stopColor="#ffc857" />
            <stop offset=".7" stopColor="#c6ff3a" />
            <stop offset="1" stopColor="#5cf3ff" />
          </linearGradient>
        </defs>
        {/* tick marks (radial dashes — NOT a ring) */}
        {Array.from({ length: 11 }, (_, i) => {
          const a = -90 + i * 18;
          const rad = (a * Math.PI) / 180;
          const x1 = 100 + Math.cos(rad) * 70;
          const y1 = 100 + Math.sin(rad) * 70;
          const x2 = 100 + Math.cos(rad) * 88;
          const y2 = 100 + Math.sin(rad) * 88;
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#c98a2b" strokeWidth={i % 5 === 0 ? 3 : 1.5} opacity={i % 5 === 0 ? 1 : 0.55} />;
        })}
        {/* single arc */}
        <path
          d="M 12 100 A 88 88 0 0 1 188 100"
          stroke={`url(#gg-${label})`}
          strokeWidth="6"
          fill="none"
          strokeLinecap="round"
          opacity=".85"
          filter="drop-shadow(0 0 6px rgba(92,243,255,.5))"
        />
        {/* needle */}
        <g className="needle" style={{ transform: `rotate(${angle}deg)`, transformOrigin: "100px 100px" }}>
          <line x1="100" y1="100" x2="100" y2="22" stroke={color} strokeWidth="4" strokeLinecap="round" />
          <circle cx="100" cy="100" r="9" fill="#0c0606" stroke="#f6d27a" strokeWidth="2" />
          <circle cx="100" cy="22" r="4" fill={color} />
        </g>
      </svg>
      <div className="gauge-readout">{v}</div>
      <div className="gauge-label">{label}</div>
    </div>
  );
}

/* --------------------- bar mixer (vertical bars) --------------------- */
function BarMixer({ bars = [], testid }) {
  return (
    <div className="barmix" data-testid={testid}>
      {bars.map((b, i) => {
        const pct = Math.max(0, Math.min(100, Number(b.value) || 0));
        const c = b.color || "#5cf3ff";
        return (
          <div className="col" key={i}>
            <div className="cap">{Number(b.raw ?? b.value ?? 0).toLocaleString()}</div>
            <div className="track" style={{ "--c": c }}>
              <div className="fill" style={{ height: `${pct}%`, "--c": c }} />
            </div>
            <div className="lbl">{b.label}</div>
          </div>
        );
      })}
    </div>
  );
}

/* --------------------- sentiment thermometer (horizontal needle) --------------------- */
function Sentiment({ score = 0, label = "neutral", reasoning = "", testid }) {
  const s = Math.max(-100, Math.min(100, Number(score) || 0));
  const left = ((s + 100) / 200) * 100;
  return (
    <div data-testid={testid}>
      <div className="thermo">
        <div className="needle" style={{ left: `calc(${left}% - 3px)` }} />
      </div>
      <div className="thermo-tick">
        <span>NEG -100</span><span>NEUTRAL 0</span><span>POS +100</span>
      </div>
      <div style={{ marginTop: 12, fontFamily: "'VT323',monospace", fontSize: 16, color: "#f4e7c2" }}>
        <span className="chip" style={{ marginRight: 8, color: "#5cf3ff", borderColor: "#5cf3ff" }}>
          {String(label || "").toUpperCase()} · {s}
        </span>
        <span style={{ opacity: .8 }}>{reasoning}</span>
      </div>
    </div>
  );
}

/* --------------------- toggle (interactive auto-refresh) --------------------- */
function Toggle({ on, onChange, testid }) {
  return (
    <div className={`toggle ${on ? "on" : ""}`} onClick={() => onChange(!on)} data-testid={testid} role="switch" aria-checked={on}>
      <div className="knob" />
    </div>
  );
}

/* --------------------- agent runner helper --------------------- */
async function runAgent(endpoint, body = null) {
  const url = `${API}/agents/${endpoint}/run`;
  if (body) return axios.post(url, body, { timeout: 120_000 });
  return axios.post(url, {}, { timeout: 120_000 });
}
async function fetchLatest(endpoint) {
  return axios.get(`${API}/agents/${endpoint}/latest`, { timeout: 15_000 });
}

/* =============================================================
   MAIN DASHBOARD COMPONENT
   ============================================================= */
export default function Dashboard() {
  const [data, setData] = useState({
    marketSignals: null, offerBuilder: null, youtubeContent: null,
    leadMagnet: null, webCrawler: null,
  });
  const [busy, setBusy] = useState({}); // per-agent running flag
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [activeVid, setActiveVid] = useState(0);
  const [crawlerUrls, setCrawlerUrls] = useState("https://lichess.org/about,https://www.chess.com/membership");
  const intervalRef = useRef(null);

  const refreshAll = useCallback(async () => {
    const results = await Promise.allSettled(AGENTS.map((a) => fetchLatest(a.endpoint)));
    const next = {};
    results.forEach((r, i) => {
      const a = AGENTS[i];
      if (r.status === "fulfilled" && r.value?.data && !r.value.data.status?.startsWith?.("empty")) {
        next[a.key] = r.value.data;
      } else if (r.status === "fulfilled") {
        next[a.key] = r.value.data; // keep empty marker
      } else {
        next[a.key] = { status: "fetch_error", error: String(r.reason) };
      }
    });
    setData(next);
    setLastRefreshed(new Date());
  }, []);

  // initial + interval
  useEffect(() => {
    refreshAll();
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (autoRefresh) {
      intervalRef.current = setInterval(refreshAll, REFRESH_MS);
    }
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, [autoRefresh, refreshAll]);

  const triggerRun = useCallback(async (endpoint, body = null) => {
    setBusy((b) => ({ ...b, [endpoint]: true }));
    try {
      await runAgent(endpoint, body);
      await refreshAll();
    } catch (e) {
      console.error(`[dashboard] run ${endpoint} failed`, e);
    } finally {
      setBusy((b) => ({ ...b, [endpoint]: false }));
    }
  }, [refreshAll]);

  /* ---------- derived "view models" from raw agent data ---------- */
  const ms = data.marketSignals?.analysis || {};
  const ob = data.offerBuilder?.analysis || {};
  const yt = data.youtubeContent?.analysis || {};
  const lm = data.leadMagnet?.analysis || {};
  const wc = data.webCrawler?.analysis || {};

  const trendStrength = Number(ms.trend_strength ?? 0);
  const sentimentScore = Number(ms.player_sentiment?.score ?? 0);
  const sentimentLabel = ms.player_sentiment?.label || "—";
  const sentimentReason = ms.player_sentiment?.reasoning || "Awaiting data";

  const revOps = (ms.revenue_opportunities || []).slice(0, 3);
  const trendingTopics = ms.top_5_trending_topics || [];
  const growth = ms.growth_indicators || {};

  const offers = (ob.offers || []).slice(0, 5);
  const scripts = (yt.scripts || []).slice(0, 8);
  const magnets = lm.magnets || [];
  const crawlerInsights = wc.cross_url_insights || [];
  const crawlerPages    = wc.per_url_analysis    || [];

  const tickerItems = useMemo(() => {
    const items = [];
    trendingTopics.forEach((t) => items.push({ tag: t.momentum?.toUpperCase() || "TREND", text: t.topic, ts: t.evidence }));
    revOps.forEach((o) => items.push({ tag: o.ranked_potential || "OPP", text: `OPP · ${o.name}`, ts: `urgency ${o.urgency_days}d` }));
    if (items.length === 0) items.push({ tag: "BOOT", text: "Awaiting Market Signals — click RUN to populate", ts: "" });
    return [...items, ...items]; // double for seamless loop
  }, [trendingTopics, revOps]);

  const alertItems = useMemo(() => {
    const out = [];
    crawlerInsights.forEach((s) => out.push({ sev: "HIGH",   text: s }));
    crawlerPages.forEach((p) => {
      const prices = (p.extracted_prices || []).join(", ");
      out.push({ sev: "MEDIUM", text: `${p.page_title || p.url} — ${prices || "no prices found"}` });
    });
    if (out.length === 0) out.push({ sev: "LOW", text: "No crawler data yet — submit URLs and click RUN" });
    return out;
  }, [crawlerInsights, crawlerPages]);

  /* ---------- growth indicator bars (vertical mixer) ---------- */
  const growthBars = [
    { label: "LICHESS\nTOURNS", raw: growth.lichess_active_tournaments,    value: Math.min(100, (growth.lichess_active_tournaments    ?? 0) / 2),  color: "#5cf3ff" },
    { label: "CHESS.COM\nSTREAMS", raw: growth.chess_com_live_streamers,   value: Math.min(100, (growth.chess_com_live_streamers      ?? 0) / 10), color: "#ff3ec8" },
    { label: "REDDIT\nVOLUME",   raw: growth.reddit_weekly_post_volume,    value: Math.min(100, (growth.reddit_weekly_post_volume     ?? 0)),       color: "#c6ff3a" },
    { label: "TRENDS\nAVG",      raw: growth.google_trends_interest_avg,   value: Math.min(100, (growth.google_trends_interest_avg    ?? 0)),       color: "#ffc857" },
  ];

  /* ---------- agent status colors ---------- */
  const statusClass = (raw) => {
    if (!raw) return "empty";
    if (raw.status === "fetch_error") return "err";
    if (raw.status === "empty" || raw.message?.startsWith?.("No reports")) return "empty";
    return "";
  };

  return (
    <div className="dash-root" data-testid="dashboard-root">
      <FontInjector />
      <div className="bg-gear g1" /><div className="bg-gear g2" /><div className="bg-gear g3" />
      <BulbString />

      {/* ===================== HEADER ===================== */}
      <header style={{ position: "relative", zIndex: 5, padding: "44px 24px 12px", textAlign: "center" }}>
        <div className="nameplate">
          <h1>4XLC LIVE AGENT COCKPIT</h1>
          <div className="sub">Steam · Brass · Neon · Realtime</div>
        </div>

        <div style={{ marginTop: 16, display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 10 }}>
          <span className="chip" data-testid="dashboard-status">
            <span className="live-dot" />
            {lastRefreshed ? `LAST SYNC ${lastRefreshed.toLocaleTimeString()}` : "Booting…"}
          </span>
          {AGENTS.map((a) => (
            <span key={a.key} className={`agent-pill ${busy[a.endpoint] ? "busy" : statusClass(data[a.key])}`} data-testid={`agent-pill-${a.endpoint}`}>
              <span className="d" /> {a.label}
            </span>
          ))}
        </div>

        <div style={{ marginTop: 14, display: "flex", flexWrap: "wrap", justifyContent: "center", alignItems: "center", gap: 12 }}>
          <button className="run-btn" data-testid="dashboard-refresh-all" onClick={refreshAll}>↻ REFRESH NOW</button>
          <span className="chip" style={{ gap: 10 }}>
            AUTO 30s
            <Toggle on={autoRefresh} onChange={setAutoRefresh} testid="dashboard-autorefresh-toggle" />
          </span>
          <button className="run-btn" data-testid="dashboard-run-all" disabled={Object.values(busy).some(Boolean)}
            onClick={async () => {
              for (const a of AGENTS.slice(0, 4)) await triggerRun(a.endpoint);
              const urls = crawlerUrls.split(",").map(u => u.trim()).filter(Boolean);
              await triggerRun("web-crawler", { urls });
            }}>
            ▶ RUN ALL AGENTS
          </button>
        </div>
      </header>

      {/* ===================== MAIN GRID ===================== */}
      <main style={{
        position: "relative", zIndex: 4,
        maxWidth: 1500, margin: "16px auto 80px", padding: "0 20px",
        display: "grid",
        gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
        gap: 20,
      }}>

        {/* ====== REVENUE OPPORTUNITIES — dominant top row ====== */}
        <section className="panel glow-gold" style={{ gridColumn: "span 12" }}>
          <div className="panel-title">
            <span>★ TOP REVENUE OPPORTUNITIES</span>
            <span className="num">$ MODULE — DOMINANT</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 16 }}>
            {[0, 1, 2].map((idx) => {
              const o = revOps[idx];
              const sev = (o?.ranked_potential || "low").toLowerCase();
              return (
                <div key={idx} className={`rev-card ${sev}`} data-testid={`rev-card-${idx}`} title={o?.rationale || ""}>
                  <span className="badge">{(o?.ranked_potential || "—")}</span>
                  <div className="ord">#{idx + 1}</div>
                  <div className="ttl">{o?.name || "— awaiting data —"}</div>
                  <div className="urg">
                    {o ? `Urgency: ${o.urgency_days || "?"}d · ${o.audience || "any audience"}` : "Run Market Signals to populate"}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* ====== TREND STRENGTH GAUGE ====== */}
        <section className="panel glow-cyan" style={{ gridColumn: "span 4" }} data-testid="widget-trend-strength">
          <div className="panel-title"><span>TREND STRENGTH</span><span className="num">◑ GAUGE</span></div>
          <div className="crt" style={{ paddingBottom: 28 }}>
            <Gauge value={trendStrength} label="HEAT 0–100" color="#5cf3ff" testid="gauge-trend" />
          </div>
          <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span className="chip">Market Signals · daily 06:00</span>
            <button className="run-btn" disabled={busy["market-signals"]} onClick={() => triggerRun("market-signals")} data-testid="run-market-signals">
              {busy["market-signals"] ? "..." : "▶ RUN"}
            </button>
          </div>
        </section>

        {/* ====== SENTIMENT METER ====== */}
        <section className="panel glow-magenta" style={{ gridColumn: "span 4" }} data-testid="widget-sentiment">
          <div className="panel-title"><span>AUDIENCE SENTIMENT</span><span className="num">≈ METER</span></div>
          <div className="crt" style={{ minHeight: 130 }}>
            <Sentiment score={sentimentScore} label={sentimentLabel} reasoning={sentimentReason} testid="sentiment-thermo" />
          </div>
        </section>

        {/* ====== GROWTH INDICATORS BAR MIXER ====== */}
        <section className="panel glow-lime" style={{ gridColumn: "span 4" }} data-testid="widget-growth">
          <div className="panel-title"><span>GROWTH INDICATORS</span><span className="num">▮ MIXER</span></div>
          <div className="crt" style={{ paddingTop: 14, paddingBottom: 14 }}>
            <BarMixer bars={growthBars} testid="barmix-growth" />
          </div>
          <div style={{ marginTop: 8, fontFamily: "'VT323',monospace", fontSize: 13, color: "#f4e7c2", opacity: .85 }}>
            Source: chess.com · lichess · Reddit · Google Trends
          </div>
        </section>

        {/* ====== TRENDING TOPICS TICKER (video ticker box #1) ====== */}
        <section className="panel glow-cyan" style={{ gridColumn: "span 12" }} data-testid="ticker-trending-topics">
          <div className="panel-title"><span>≋ TRENDING TOPICS — LIVE TICKER</span><span className="num">VIDEO BOX 1/3</span></div>
          <div className="ticker">
            <div className="rail">
              {tickerItems.map((it, i) => (
                <span className="ticker-item" key={i}>
                  <span className="tag">{it.tag}</span> {it.text} <span className="dot">●</span> <span style={{ opacity: .7 }}>{it.ts}</span>
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ====== YOUTUBE SCRIPT QUEUE (video ticker box #2 — multiple bordered video boxes) ====== */}
        <section className="panel glow-magenta" style={{ gridColumn: "span 8" }} data-testid="widget-youtube-queue">
          <div className="panel-title"><span>▶ YOUTUBE SCRIPT QUEUE</span><span className="num">VIDEO BOX 2/3</span></div>
          <div className="vid-row">
            {scripts.length === 0 && (
              <div className="vid-card" style={{ opacity: .7 }}>
                <div className="vid-screen">Awaiting YouTube Content Agent…<br />Click RUN below.</div>
                <div className="title">— no scripts yet —</div>
              </div>
            )}
            {scripts.map((s, i) => (
              <div key={i}
                   className={`vid-card ${i === activeVid ? "active" : ""}`}
                   onClick={() => setActiveVid(i)}
                   data-testid={`video-script-card-${i}`}>
                <div className="vid-screen">
                  <div style={{ fontSize: 11, color: "#ffc857", textShadow: "0 0 6px #ffc857", marginBottom: 4 }}>HOOK ▶</div>
                  {(s.hook_5s || "—").slice(0, 110)}
                </div>
                <div className="title">{s.title || "(untitled)"}</div>
                <div className="vid-meta">
                  <span className="l">{s.format_length_sec || 60}s</span>
                  <span>{(s.platforms || []).slice(0, 2).join("+") || "shorts"}</span>
                  <span>{s.post_time_pt || "—"} PT</span>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
            <span className="chip">YouTube Content · daily 08:00</span>
            <button className="run-btn" disabled={busy["youtube-content"]} onClick={() => triggerRun("youtube-content")} data-testid="run-youtube-content">
              {busy["youtube-content"] ? "..." : "▶ RUN"}
            </button>
          </div>
        </section>

        {/* ====== OFFER STATUS ====== */}
        <section className="panel glow-cyan" style={{ gridColumn: "span 4" }} data-testid="widget-offer-status">
          <div className="panel-title"><span>$ OFFER STATUS</span><span className="num">⊕ MODULE</span></div>
          <div style={{ maxHeight: 360, overflowY: "auto" }}>
            {offers.length === 0 && (
              <div style={{ opacity: .7, fontFamily: "'VT323',monospace", fontSize: 16, padding: 8 }}>
                No offers built yet.
              </div>
            )}
            {offers.map((o, i) => (
              <div key={i} className="offer-card" data-testid={`offer-card-${i}`}>
                <div className="offer-head">
                  <div className="offer-name">{o.name}</div>
                  <div className="offer-price">${(o.price_usd ?? 0).toFixed(2)}</div>
                </div>
                <div className="offer-meta">
                  <span className="fit-pill">FIT {o.market_fit_score ?? 0}</span>
                  <span>{o.ranked_potential || "—"}</span>
                  <span>{o.urgency_days ?? "?"}d</span>
                </div>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
            <span className="chip">Offer Builder · daily 07:00</span>
            <button className="run-btn" disabled={busy["offer-builder"]} onClick={() => triggerRun("offer-builder")} data-testid="run-offer-builder">
              {busy["offer-builder"] ? "..." : "▶ RUN"}
            </button>
          </div>
        </section>

        {/* ====== LEAD MAGNET PERFORMANCE ====== */}
        <section className="panel glow-lime" style={{ gridColumn: "span 6" }} data-testid="widget-lead-magnet">
          <div className="panel-title"><span>⌂ LEAD MAGNET PERFORMANCE</span><span className="num">⇩ MAGNETS</span></div>
          {magnets.length === 0 && (
            <div style={{ opacity: .7, fontFamily: "'VT323',monospace", fontSize: 16 }}>No magnets generated yet.</div>
          )}
          {magnets.map((m, i) => (
            <div key={i} className="magnet-card" data-testid={`magnet-card-${m.audience || i}`}>
              <div className="magnet-h">
                <span className="aud">{m.audience || "—"}</span>
                <span className="rate">{m.expected_capture_rate_pct ?? "?"}% CAPTURE</span>
              </div>
              <div style={{ fontFamily: "'Special Elite',monospace", fontSize: 14, color: "#f4e7c2" }}>
                {m.title || "—"}
              </div>
              <div style={{ fontFamily: "'VT323',monospace", fontSize: 13, color: "#f4e7c2", opacity: .85, marginTop: 4 }}>
                {m.format || "PDF"} · {m.page_count_est || "?"} pages · {(m.email_sequence || []).length} emails · {m.url_slug || "—"}
              </div>
              <div style={{ fontFamily: "'VT323',monospace", fontSize: 13, color: "#c6ff3a", marginTop: 4, opacity: .9 }}>
                Pain: {m.pain_point || "—"}
              </div>
            </div>
          ))}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
            <span className="chip">Lead Magnet · daily 09:00</span>
            <button className="run-btn" disabled={busy["lead-magnet"]} onClick={() => triggerRun("lead-magnet")} data-testid="run-lead-magnet">
              {busy["lead-magnet"] ? "..." : "▶ RUN"}
            </button>
          </div>
        </section>

        {/* ====== WEB CRAWLER ALERTS (video ticker box #3) ====== */}
        <section className="panel glow-magenta" style={{ gridColumn: "span 6" }} data-testid="widget-web-crawler">
          <div className="panel-title"><span>⚡ WEB CRAWLER ALERTS</span><span className="num">VIDEO BOX 3/3</span></div>
          <div className="ticker" style={{ height: 56 }}>
            <div className="rail">
              {alertItems.concat(alertItems).map((a, i) => (
                <span className="ticker-item" key={i}>
                  <span className={`sev ${a.sev}`} style={{ fontFamily: "'Bungee Shade',cursive", fontSize: 9, padding: "2px 7px", borderRadius: 4, background: a.sev === "HIGH" ? "#ff5b4a" : a.sev === "MEDIUM" ? "#ffc857" : "#c6ff3a", color: "#0c0606", letterSpacing: ".12em" }}>{a.sev}</span>
                  {a.text}
                  <span className="dot">●</span>
                </span>
              ))}
            </div>
          </div>
          <div style={{ marginTop: 12, maxHeight: 200, overflowY: "auto" }}>
            {crawlerPages.map((p, i) => (
              <div key={i} className="alert-row" data-testid={`crawler-page-${i}`}>
                <span className={`sev ${p.fetch_status === "ok" ? "MEDIUM" : "HIGH"}`}>{p.fetch_status?.toUpperCase() || "?"}</span>
                <span style={{ color: "#f4e7c2", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.page_title || p.url}
                  {p.extracted_prices?.length ? ` · ${p.extracted_prices.slice(0, 3).join(" / ")}` : ""}
                </span>
              </div>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
            <input
              className="url-input"
              data-testid="crawler-url-input"
              placeholder="https://comp1.com, https://comp2.com"
              value={crawlerUrls}
              onChange={(e) => setCrawlerUrls(e.target.value)}
            />
            <button className="run-btn" disabled={busy["web-crawler"]} data-testid="run-web-crawler"
              onClick={() => {
                const urls = crawlerUrls.split(",").map((u) => u.trim()).filter(Boolean);
                triggerRun("web-crawler", { urls });
              }}>
              {busy["web-crawler"] ? "..." : "▶ CRAWL"}
            </button>
          </div>
          <div style={{ marginTop: 8, fontFamily: "'VT323',monospace", fontSize: 13, color: "#f4e7c2", opacity: .8 }}>
            Web Crawler · every 6h (00·06·12·18) · market-agnostic — any URL works
          </div>
        </section>

      </main>

      <footer style={{ position: "relative", zIndex: 4, textAlign: "center", padding: "20px 0 40px",
                       fontFamily: "'Major Mono Display',monospace", fontSize: 11,
                       color: "#f4e7c2", opacity: .55, letterSpacing: ".2em" }}>
        4XLC · CENTRAL HUB · LIVE AGENT TELEMETRY · {new Date().getFullYear()}
      </footer>
    </div>
  );
}
